/**
 * Subscription Service
 * Handles subscription plan and shop subscription management
 * SUPER_ADMIN only features
 */

import { getApiClient } from './client';
import { createLogger } from '../config/environment';

const logger = createLogger('SubscriptionService');

// ==================== Types ====================

export interface SubscriptionPlan {
  id?: string;
  planName: string;
  subscriptionDays: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionPlansResponse {
  status: string;
  message: string;
  data: SubscriptionPlan[];
  count?: number;
}

export interface SubscriptionPlanResponse {
  status: string;
  message: string;
  data: SubscriptionPlan;
}

export interface ShopSubscription {
  id?: string;
  shopId: string;
  planId: string;
  planName?: string;
  planStartDate: string;
  planEndDate: string;
  isActive: boolean;
  autoRenew: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShopSubscriptionsResponse {
  status: string;
  message: string;
  data: ShopSubscription[];
  count?: number;
}

export interface ShopSubscriptionResponse {
  status: string;
  message: string;
  data: ShopSubscription;
}

export interface CreateShopSubscriptionRequest {
  shopId: string;
  planId: string;
  planStartDate?: string;
  planEndDate?: string;
  isActive?: boolean;
  autoRenew?: boolean;
  partnerId?: string; // Optional - Partner ID who created this subscription
}

export interface UpdateShopSubscriptionRequest {
  planId?: string;
  planStartDate?: string;
  planEndDate?: string;
  isActive?: boolean;
  autoRenew?: boolean;
}

// ==================== Subscription Service ====================

export class SubscriptionService {
  private client = getApiClient();

  // ==================== Subscription Plans ====================

  /**
   * Get all subscription plans
   * SUPER_ADMIN only
   */
  async getSubscriptionPlans(activeOnly: boolean = false): Promise<SubscriptionPlan[]> {
    try {
      logger.info('Fetching subscription plans', { activeOnly });

      // API returns array directly, not wrapped
      const response = await this.client.get<any>(
        activeOnly ? '/api/subscription-plans/active' : '/api/subscription-plans'
      );

      logger.info('🔍 Raw API response:', response);
      logger.info('🔍 Response type:', typeof response);
      logger.info('🔍 Is array?:', Array.isArray(response));

      // Handle both wrapped and unwrapped responses
      const plans = Array.isArray(response) ? response : (response?.data || []);

      logger.info('✅ Plans fetched successfully', { count: plans.length });
      return plans;
    } catch (err) {
      logger.error('Failed to fetch plans', err);
      throw new Error('Failed to load subscription plans');
    }
  }

  /**
   * Get subscription plan by ID
   * SUPER_ADMIN only
   */
  async getSubscriptionPlanById(planId: string): Promise<SubscriptionPlan> {
    try {
      logger.info('Fetching plan by ID', { planId });

      // API returns object directly, not wrapped
      const plan = await this.client.get<SubscriptionPlan>(
        `/api/subscription-plans/${planId}`
      );

      return plan;
    } catch (err) {
      logger.error('Failed to fetch plan', err);
      throw new Error('Failed to load subscription plan');
    }
  }

  /**
   * Create new subscription plan
   * SUPER_ADMIN only
   */
  async createSubscriptionPlan(plan: SubscriptionPlan): Promise<SubscriptionPlan> {
    try {
      logger.info('Creating subscription plan', { planName: plan.planName });

      // API returns object directly, not wrapped
      const createdPlan = await this.client.post<SubscriptionPlan>(
        '/api/subscription-plans',
        plan
      );

      logger.info('✅ Plan created successfully');
      return createdPlan;
    } catch (err) {
      logger.error('Failed to create plan', err);
      throw new Error('Failed to create subscription plan');
    }
  }

  /**
   * Update subscription plan
   * SUPER_ADMIN only
   */
  async updateSubscriptionPlan(planId: string, plan: Partial<SubscriptionPlan>): Promise<SubscriptionPlan> {
    try {
      logger.info('Updating subscription plan', { planId });

      // API returns object directly, not wrapped
      const updatedPlan = await this.client.put<SubscriptionPlan>(
        `/api/subscription-plans/${planId}`,
        plan
      );

      logger.info('✅ Plan updated successfully');
      return updatedPlan;
    } catch (err) {
      logger.error('Failed to update plan', err);
      throw new Error('Failed to update subscription plan');
    }
  }

  /**
   * Delete subscription plan
   * SUPER_ADMIN only
   */
  async deleteSubscriptionPlan(planId: string): Promise<void> {
    try {
      logger.info('Deleting subscription plan', { planId });

      await this.client.delete(`/api/subscription-plans/${planId}`);

      logger.info('✅ Plan deleted successfully');
    } catch (err) {
      logger.error('Failed to delete plan', err);
      throw new Error('Failed to delete subscription plan');
    }
  }

  // ==================== Shop Subscriptions ====================

  /**
   * Get all shop subscriptions for a specific shop
   * SUPER_ADMIN only
   * Endpoint: GET /api/shop-subscriptions/shop/{shopId}
   */
  async getShopSubscriptions(shopId: string): Promise<ShopSubscription[]> {
    try {
      logger.info('Fetching shop subscriptions', { shopId });

      // API returns array directly, not wrapped in response object
      const response = await this.client.get<ShopSubscription[]>(
        `/api/shop-subscriptions/shop/${shopId}`
      );

      logger.info('🔍 Raw shop subscriptions response:', response);
      logger.info('🔍 Response type:', typeof response);
      logger.info('🔍 Is array?:', Array.isArray(response));

      // Handle both wrapped and unwrapped responses
      const subscriptions = Array.isArray(response) ? response : (response?.data || []);

      logger.info('✅ Shop subscriptions fetched', { count: subscriptions.length });
      if (subscriptions.length > 0) {
        logger.info('🔍 First subscription:', subscriptions[0]);
      }

      return subscriptions;
    } catch (err) {
      logger.error('Failed to fetch shop subscriptions', err);
      throw new Error('Failed to load shop subscriptions');
    }
  }

  /**
   * Get all shop subscriptions (all shops)
   * SUPER_ADMIN only
   * Endpoint: GET /api/shop-subscriptions
   */
  async getAllShopSubscriptions(): Promise<ShopSubscription[]> {
    try {
      logger.info('Fetching all shop subscriptions');

      // API returns array directly, not wrapped in response object
      const subscriptions = await this.client.get<ShopSubscription[]>(
        '/api/shop-subscriptions'
      );

      logger.info('✅ All shop subscriptions fetched', { count: subscriptions.length });
      return subscriptions;
    } catch (err) {
      logger.error('Failed to fetch all shop subscriptions', err);
      throw new Error('Failed to load all shop subscriptions');
    }
  }

  /**
   * Get shop subscription by ID
   * SUPER_ADMIN only
   */
  async getShopSubscriptionById(subscriptionId: string): Promise<ShopSubscription> {
    try {
      logger.info('Fetching shop subscription by ID', { subscriptionId });

      // API returns object directly, not wrapped
      const subscription = await this.client.get<ShopSubscription>(
        `/api/shop-subscriptions/${subscriptionId}`
      );

      return subscription;
    } catch (err) {
      logger.error('Failed to fetch shop subscription', err);
      throw new Error('Failed to load shop subscription');
    }
  }

  /**
   * Assign subscription to shop
   * SUPER_ADMIN only
   */
  async createShopSubscription(request: CreateShopSubscriptionRequest): Promise<ShopSubscription> {
    try {
      logger.info('Creating shop subscription', { shopId: request.shopId, planId: request.planId });

      // API returns object directly, not wrapped
      const subscription = await this.client.post<ShopSubscription>(
        '/api/shop-subscriptions',
        request
      );

      logger.info('✅ Shop subscription created successfully');
      return subscription;
    } catch (err) {
      logger.error('Failed to create shop subscription', err);
      throw new Error('Failed to assign subscription to shop');
    }
  }

  /**
   * Update shop subscription
   * SUPER_ADMIN only
   */
  async updateShopSubscription(
    subscriptionId: string,
    request: UpdateShopSubscriptionRequest
  ): Promise<ShopSubscription> {
    try {
      logger.info('Updating shop subscription', { subscriptionId });

      // API returns object directly, not wrapped
      const subscription = await this.client.put<ShopSubscription>(
        `/api/shop-subscriptions/${subscriptionId}`,
        request
      );

      logger.info('✅ Shop subscription updated successfully');
      return subscription;
    } catch (err) {
      logger.error('Failed to update shop subscription', err);
      throw new Error('Failed to update shop subscription');
    }
  }

  /**
   * Delete shop subscription
   * SUPER_ADMIN only
   */
  async deleteShopSubscription(subscriptionId: string): Promise<void> {
    try {
      logger.info('Deleting shop subscription', { subscriptionId });

      await this.client.delete(`/api/shop-subscriptions/${subscriptionId}`);

      logger.info('✅ Shop subscription deleted successfully');
    } catch (err) {
      logger.error('Failed to delete shop subscription', err);
      throw new Error('Failed to delete shop subscription');
    }
  }

  /**
   * Renew shop subscription
   * SUPER_ADMIN only
   */
  async renewShopSubscription(shopId: string, planId: string): Promise<ShopSubscription> {
    try {
      logger.info('Renewing shop subscription', { shopId, planId });

      const subscription = await this.client.post<ShopSubscription>(
        '/api/shop-subscriptions/renew',
        { shopId, planId }
      );

      logger.info('✅ Shop subscription renewed successfully');
      return subscription;
    } catch (err) {
      logger.error('Failed to renew shop subscription', err);
      throw new Error('Failed to renew shop subscription');
    }
  }
}

// Singleton instance
let subscriptionServiceInstance: SubscriptionService | null = null;

export const getSubscriptionService = (): SubscriptionService => {
  if (!subscriptionServiceInstance) {
    subscriptionServiceInstance = new SubscriptionService();
  }
  return subscriptionServiceInstance;
};

export const resetSubscriptionService = (): void => {
  subscriptionServiceInstance = null;
};

// Export singleton instance
export const subscriptionService = getSubscriptionService();

